<template>
  <div
    :class="isEmbedded ? 'p-3 sm:p-4 space-y-4' : 'container mx-auto p-4 sm:p-6 space-y-6'"
    class="flex-1 w-full bg-gray-50 dark:bg-gray-900 overflow-auto"
  >
    <!-- Header -->
    <div
      class="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-gray-200 dark:border-gray-800"
    >
      <div>
        <div class="flex items-center gap-2">
          <UIcon
            :name="isTransactionMode ? 'i-lucide-receipt' : 'i-lucide-shield-check'"
            class="w-6 h-6 text-primary"
          />
          <h1 class="text-xl font-bold text-gray-900 dark:text-white">
            <template v-if="isTransactionMode">
              USt-IdNr.-Prüfung Vorgang {{ resolvedTransactionNr }}
            </template>
            <template v-else>
              USt-IdNr.-Prüfung Adresse {{ resolvedAddressNr }}
            </template>
          </h1>
        </div>

        <!-- Context Badges -->
        <div class="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1.5">
          <span v-if="isTransactionMode && txSummary?.transactionType" class="font-medium">
            Art: <span class="font-semibold text-gray-800 dark:text-gray-200">{{ txSummary.transactionType }}</span>
          </span>
          <span v-if="isTransactionMode && resolvedTransactionNr" class="font-mono">
            Beleg: <span class="font-semibold text-gray-800 dark:text-gray-200">{{ resolvedTransactionNr }}</span>
          </span>
          <span v-if="resolvedAddressNr">
            AdrNr: <span class="font-semibold text-gray-800 dark:text-gray-200">{{ resolvedAddressNr }}</span>
          </span>
          <span v-if="activeVatId">
            USt-IdNr: <span class="font-mono font-semibold text-gray-900 dark:text-white">{{ activeVatId }}</span>
          </span>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <UButton
          v-if="canValidate"
          color="primary"
          icon="i-lucide-refresh-cw"
          :loading="validating"
          @click="triggerValidation"
        >
          <template v-if="isTransactionMode && isUnvalidatedTransaction">
            Jetzt für diesen Vorgang prüfen
          </template>
          <template v-else>
            Jetzt prüfen
          </template>
        </UButton>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center py-12">
      <UIcon name="i-lucide-loader-circle" class="w-8 h-8 animate-spin text-primary" />
    </div>

    <!-- Error State -->
    <div
      v-else-if="fetchError"
      class="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 p-6 text-center"
    >
      <UIcon name="i-lucide-alert-circle" class="w-10 h-10 text-red-500 mx-auto mb-2" />
      <h3 class="text-base font-semibold text-red-700 dark:text-red-300">
        Prüfstatus konnte nicht geladen werden
      </h3>
      <p class="text-sm text-red-600 dark:text-red-400 mt-1">{{ fetchError }}</p>
      <UButton class="mt-4" color="neutral" variant="soft" size="sm" @click="fetchData">
        Erneut versuchen
      </UButton>
    </div>

    <!-- Empty State (No validation exists at all) -->
    <div
      v-else-if="!statusSummary && (!txSummary || isUnvalidatedTransaction && !txSummary.latestAddressValidation)"
      class="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center bg-white dark:bg-gray-800"
    >
      <UIcon name="i-lucide-search" class="w-10 h-10 text-gray-400 mx-auto mb-2" />
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">
        Keine bisherige Prüfung vorhanden
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Für diesen Eintrag wurde bisher noch keine offizielle USt-IdNr.-Prüfung durchgeführt.
      </p>
      <UButton
        v-if="canValidate"
        class="mt-4"
        color="primary"
        icon="i-lucide-check-circle"
        :loading="validating"
        @click="triggerValidation"
      >
        Erste Prüfung durchführen
      </UButton>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Unvalidated Transaction Callout -->
      <div
        v-if="isTransactionMode && isUnvalidatedTransaction"
        class="rounded-xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4"
      >
        <div class="flex items-start gap-3">
          <UIcon name="i-lucide-alert-triangle" class="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <h4 class="text-sm font-semibold text-amber-800 dark:text-amber-200">
              Vorgang noch nicht geprüft
            </h4>
            <p class="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              Für diesen Beleg ({{ resolvedTransactionNr }}) ist noch kein Prüfprotokoll hinterlegt.
              Unten sehen Sie die bisherigen Prüfungen der zugeordneten Adresse {{ resolvedAddressNr }}.
            </p>
          </div>
        </div>
        <UButton
          v-if="canValidate"
          color="warning"
          variant="solid"
          size="sm"
          icon="i-lucide-play"
          :loading="validating"
          @click="triggerValidation"
        >
          Jetzt für Vorgang prüfen
        </UButton>
      </div>

      <!-- Letzte Prüfung & Qualifizierte Merkmale -->
      <UCard>
        <template #header>
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <UBadge :color="statusBadgeColor" size="lg" variant="solid" class="font-bold">
                {{ effectiveStatusDisplay }}
              </UBadge>
              <div>
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  <template v-if="isTransactionMode && !isUnvalidatedTransaction">
                    Prüfung dieses Vorgangs
                  </template>
                  <template v-else-if="isTransactionMode && isUnvalidatedTransaction">
                    Letzte Prüfung der Adresse
                  </template>
                  <template v-else>
                    Letzte Prüfung
                  </template>
                </p>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ formatTimestamp(activeCheckTimestamp) }}
                </p>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <!-- Reuse badge in transaction mode -->
              <template v-if="isTransactionMode && !isUnvalidatedTransaction && txSummary">
                <UBadge
                  v-if="txSummary.reused"
                  color="info"
                  variant="subtle"
                  size="sm"
                  class="flex items-center gap-1 font-medium"
                >
                  <UIcon name="i-lucide-copy" class="w-3.5 h-3.5" />
                  <span>Wiederverwendet aus Prüfung {{ txSummary.reusedFromValidationId }}</span>
                </UBadge>
                <UBadge
                  v-else
                  color="success"
                  variant="subtle"
                  size="sm"
                  class="flex items-center gap-1 font-medium"
                >
                  <UIcon name="i-lucide-check-circle" class="w-3.5 h-3.5" />
                  <span>Neu geprüft für diesen Beleg</span>
                </UBadge>
              </template>

              <span v-if="activeValidationId" class="font-mono text-xs">
                Prüf-ID: {{ activeValidationId }}
              </span>
              <UButton
                v-if="activeValidationId"
                size="xs"
                color="neutral"
                variant="soft"
                icon="i-lucide-external-link"
                @click="viewProtocol(activeValidationId)"
              >
                Protokoll anzeigen
              </UButton>
            </div>
          </div>
        </template>

        <!-- German Domestic Formal Check: Aufgeräumte & einfache Übersicht -->
        <div v-if="isGermanFormal" class="space-y-4">
          <!-- Ergebnis-Karte -->
          <div
            :class="[
              isValidFormalVat
                ? 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/50 dark:bg-emerald-950/20'
                : 'border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20'
            ]"
            class="rounded-xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div class="flex items-start gap-3.5">
              <div
                :class="[
                  isValidFormalVat
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-900/50 dark:text-red-400'
                ]"
                class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
              >
                <UIcon
                  :name="isValidFormalVat ? 'i-lucide-check-circle-2' : 'i-lucide-alert-circle'"
                  class="w-5 h-5"
                />
              </div>
              <div class="space-y-1">
                <div class="flex items-center gap-2 flex-wrap">
                  <h4 class="text-sm font-bold text-gray-900 dark:text-white">
                    {{
                      isValidFormalVat
                        ? 'Deutsche USt-IdNr. ist formal gültig'
                        : (activeVatId ? 'Ungültiges USt-IdNr.-Format' : 'Keine USt-IdNr. hinterlegt')
                    }}
                  </h4>
                  <UBadge
                    :color="isValidFormalVat ? 'success' : 'error'"
                    variant="subtle"
                    size="xs"
                    class="font-semibold"
                  >
                    {{ isValidFormalVat ? 'Inland: Gültig' : (activeVatId ? 'Inland: Ungültig' : 'Keine USt-IdNr.') }}
                  </UBadge>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-300">
                  <template v-if="isValidFormalVat">
                    Geprüfte USt-IdNr.: <span class="font-mono font-bold text-gray-900 dark:text-white">{{ activeVatId }}</span>
                    (entspricht dem Schema DE + 9 Ziffern).
                  </template>
                  <template v-else-if="activeVatId">
                    Der Wert <span class="font-mono font-bold text-gray-900 dark:text-white">{{ activeVatId }}</span>
                    entspricht nicht dem deutschen Format (DE + 9 Ziffern).
                  </template>
                  <template v-else-if="isCountryDomestic">
                    Für diese inländische Rechnungsanschrift ist keine USt-IdNr. hinterlegt.
                  </template>
                  <template v-else>
                    Für diese ausländische Rechnungsanschrift ist keine USt-IdNr. hinterlegt.
                  </template>
                </p>
              </div>
            </div>

            <!-- Prüfmethode-Tag -->
            <div class="flex sm:flex-col items-start sm:items-end gap-1 text-xs text-gray-500 shrink-0">
              <template v-if="activeVatId">
                <span class="inline-flex items-center gap-1 font-mono text-[11px] bg-white/80 dark:bg-gray-800/80 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                  <UIcon name="i-lucide-binary" class="w-3.5 h-3.5 text-primary" />
                  Regex: ^DE\d{9}$
                </span>
                <span class="text-[11px] text-gray-400">
                  Inländische Syntaxprüfung
                </span>
              </template>
              <template v-else>
                <span class="inline-flex items-center gap-1 font-mono text-[11px] bg-white/80 dark:bg-gray-800/80 px-2 py-1 rounded border border-gray-200 dark:border-gray-700">
                  <UIcon name="i-lucide-alert-circle" class="w-3.5 h-3.5 text-amber-500" />
                  Keine USt-IdNr.
                </span>
                <span class="text-[11px] text-gray-400">
                  Stammdatenprüfung
                </span>
              </template>
            </div>
          </div>

          <!-- Kompakter Hinweis zu BZSt -->
          <div class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-900/40 text-xs text-blue-900 dark:text-blue-300">
            <UIcon name="i-lucide-info" class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
            <p v-if="!activeVatId">
              <strong>Keine USt-IdNr. hinterlegt:</strong>
              Es erfolgt <strong>keine Abfrage beim Bundeszentralamt für Steuern (BZSt)</strong>, da ohne hinterlegte USt-IdNr. keine amtliche Bestätigungsprüfung durchgeführt werden kann.
            </p>
            <p v-else>
              <strong>Formelle Inlandsprüfung:</strong>
              Es erfolgt <strong>keine Abfrage beim Bundeszentralamt für Steuern (BZSt)</strong>, da das BZSt Bestätigungen nur für innergemeinschaftliche EU-Geschäfte anbietet. Bei deutschen Partnern wird das Nummernformat nach <code class="font-mono text-[11px] font-semibold">^DE\d{9}$</code> geprüft.
            </p>
          </div>
        </div>

        <!-- Official Status message if present (nur bei ausländischen EU-Prüfungen) -->
        <div v-if="!isGermanFormal && officialStatusMessage" class="mb-4 pb-3 border-b border-gray-100 dark:border-gray-800 text-xs">
          <div class="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <span class="font-semibold uppercase tracking-wider text-gray-400 text-[11px]">
              BZSt-Rückmeldung:
            </span>
            <span v-if="officialStatusCode" class="font-mono font-bold text-primary">{{ officialStatusCode }}</span>
            <span>–</span>
            <span class="font-medium">{{ officialStatusMessage }}</span>
          </div>
        </div>

        <!-- Qualifizierte Adressprüfung Matrix (Nur bei ausländischen EU-Prüfungen) -->
        <div v-if="!isGermanFormal && effectiveComparisonData && hasAnyMatchData" class="space-y-3">
          <div class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Ergebnis der qualifizierten Adressprüfung
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Firmenname -->
            <div
              class="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-800"
            >
              <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">Firmenname</p>
              <div class="flex items-center gap-2 mt-2">
                <UIcon
                  :name="getMatchIcon(effectiveComparisonData.companyNameMatch)"
                  :class="getMatchColor(effectiveComparisonData.companyNameMatch)"
                  class="w-5 h-5 shrink-0"
                />
                <span class="text-sm font-semibold">
                  {{ getMatchLabel(effectiveComparisonData.companyNameMatch) }}
                </span>
              </div>
              <p
                class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate"
                :title="effectiveSubmittedData?.companyName"
              >
                {{ effectiveSubmittedData?.companyName || '-' }}
              </p>
            </div>

            <!-- Straße -->
            <div
              class="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-800"
            >
              <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">Straße</p>
              <div class="flex items-center gap-2 mt-2">
                <UIcon
                  :name="getMatchIcon(effectiveComparisonData.streetMatch)"
                  :class="getMatchColor(effectiveComparisonData.streetMatch)"
                  class="w-5 h-5 shrink-0"
                />
                <span class="text-sm font-semibold">
                  {{ getMatchLabel(effectiveComparisonData.streetMatch) }}
                </span>
              </div>
              <p
                class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate"
                :title="effectiveSubmittedData?.street"
              >
                {{ effectiveSubmittedData?.street || '-' }}
              </p>
            </div>

            <!-- PLZ -->
            <div
              class="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-800"
            >
              <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">PLZ</p>
              <div class="flex items-center gap-2 mt-2">
                <UIcon
                  :name="getMatchIcon(effectiveComparisonData.zipMatch)"
                  :class="getMatchColor(effectiveComparisonData.zipMatch)"
                  class="w-5 h-5 shrink-0"
                />
                <span class="text-sm font-semibold">
                  {{ getMatchLabel(effectiveComparisonData.zipMatch) }}
                </span>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                {{ effectiveSubmittedData?.zip || '-' }}
              </p>
            </div>

            <!-- Ort -->
            <div
              class="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-800"
            >
              <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">Ort</p>
              <div class="flex items-center gap-2 mt-2">
                <UIcon
                  :name="getMatchIcon(effectiveComparisonData.cityMatch)"
                  :class="getMatchColor(effectiveComparisonData.cityMatch)"
                  class="w-5 h-5 shrink-0"
                />
                <span class="text-sm font-semibold">
                  {{ getMatchLabel(effectiveComparisonData.cityMatch) }}
                </span>
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                {{ effectiveSubmittedData?.city || '-' }}
              </p>
            </div>
          </div>
        </div>
      </UCard>

      <!-- History Table (All previous validations for the address) -->
      <UCard>
        <template #header>
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex items-center gap-2">
              <UIcon name="i-lucide-history" class="w-5 h-5 text-gray-500" />
              <h3 class="text-base font-semibold">
                Prüfungshistorie der Adresse {{ resolvedAddressNr }} (Unveränderbare S3 Protokolle)
              </h3>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <UTabs
                v-if="hasBothCheckTypes"
                v-model="historyFilter"
                :items="historyFilterItems"
                :content="false"
                variant="pill"
                size="xs"
                data-test="history-filter"
              />
              <UBadge color="neutral" variant="subtle" size="xs">
                {{ filteredHistoryItems.length }} {{ filteredHistoryItems.length === 1 ? 'Prüfung' : 'Prüfungen' }}
              </UBadge>
            </div>
          </div>
        </template>

        <div v-if="historyItems.length === 0" class="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
          Keine historischen Prüfungen für diese Adresse vorhanden.
        </div>

        <div
          v-else-if="filteredHistoryItems.length === 0"
          class="p-6 text-center text-gray-500 dark:text-gray-400 text-sm"
        >
          {{ historyFilter === 'transaction' ? 'Keine Vorgangsprüfungen vorhanden.' : 'Keine Adressprüfungen vorhanden.' }}
        </div>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-left text-sm">
            <thead
              class="border-b border-gray-200 dark:border-gray-800 text-xs uppercase text-gray-400 font-semibold"
            >
              <tr>
                <th class="py-3 px-4">Typ</th>
                <th class="py-3 px-4">Zeitpunkt</th>
                <th class="py-3 px-4">Auslöser</th>
                <th class="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
              <tr
                v-for="item in filteredHistoryItems"
                :key="item.validationId || item.timestamp"
                :data-test="isTransactionCheck(item) ? 'history-row-transaction' : 'history-row-address'"
                :class="[getHistoryRowClass(item), { 'cursor-pointer': item.validationId }]"
                :tabindex="item.validationId ? 0 : undefined"
                :role="item.validationId ? 'button' : undefined"
                :title="item.validationId ? 'Prüfprotokoll öffnen' : undefined"
                :aria-label="item.validationId ? `Prüfprotokoll vom ${formatTimestamp(item.timestamp)} öffnen` : undefined"
                @click="viewProtocol(item.validationId)"
                @keydown.enter.prevent="viewProtocol(item.validationId)"
                @keydown.space.prevent="viewProtocol(item.validationId)"
              >
                <td class="py-3 px-4">
                  <UBadge
                    v-if="isTransactionCheck(item)"
                    color="secondary"
                    variant="subtle"
                    size="xs"
                    icon="i-lucide-file-text"
                  >
                    Vorgang
                  </UBadge>
                  <UBadge v-else color="neutral" variant="subtle" size="xs" icon="i-lucide-map-pin">
                    Adresse
                  </UBadge>
                </td>
                <td class="py-3 px-4 font-medium">
                  {{ formatTimestamp(item.timestamp) }}
                </td>
                <td class="py-3 px-4">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="text-xs text-gray-600 dark:text-gray-300">
                      {{ formatTrigger(item.trigger) }}
                    </span>
                    <UBadge
                      v-if="isTransactionCheck(item) && item.transactionNo"
                      color="secondary"
                      variant="outline"
                      size="xs"
                      class="font-mono"
                      data-test="transaction-ref"
                    >
                      {{ formatTransactionRef(item) }}
                    </UBadge>
                    <span
                      v-if="item.reusedFromValidationId"
                      data-test="reuse-marker"
                      role="img"
                      class="inline-flex text-secondary cursor-help"
                      :title="getReusedFromTitle(item)"
                      :aria-label="getReusedFromTitle(item)"
                    >
                      <UIcon name="i-lucide-refresh-cw" class="w-3.5 h-3.5" />
                    </span>
                    <UBadge
                      v-if="isMatchingCurrentTransaction(item)"
                      color="primary"
                      variant="solid"
                      size="xs"
                      class="font-semibold"
                    >
                      Dieser Beleg
                    </UBadge>
                  </div>
                </td>
                <td class="py-2 px-4">
                  <div class="flex flex-col divide-y divide-gray-200 dark:divide-gray-700 w-fit min-w-36">
                    <div class="flex items-center gap-2 flex-wrap pb-1.5">
                      <UBadge
                        :color="getHistoryItemColor(item.status, item)"
                        variant="soft"
                        size="xs"
                      >
                        {{ getHistoryItemDisplay(item) }}
                      </UBadge>
                      <span
                        v-if="hasMatchResults(item) && !isItemGermanFormal(item)"
                        data-test="a-match-percentage"
                        class="text-[11px] font-mono"
                        :class="getAMatchPercentageColor(item)"
                        :title="getAMatchPercentageTitle(item)"
                      >
                        {{ getAMatchPercentage(item) }}%
                      </span>
                    </div>
                    <div class="pt-1.5 text-xs" data-test="history-status-code">
                      <UBadge
                        v-if="item.officialStatusCode"
                        :color="isItemGermanFormal(item) ? (item.valid ? 'success' : 'error') : 'primary'"
                        variant="subtle"
                        size="xs"
                        class="font-medium"
                      >
                        {{ isItemGermanFormal(item) ? formatFormalStatusCode(item.officialStatusCode) : item.officialStatusCode }}
                      </UBadge>
                      <span v-else class="text-gray-400">Kein Prüfcode</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </UCard>
    </div>

    <!-- Protocol Details Modal -->
    <VatProtocolModal
      v-model:open="isProtocolModalOpen"
      :protocol="selectedProtocol"
      title="Prüfprotokoll Details"
    />
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getActivePinia } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import {
  vatValidationApi,
  type AddressStatusSummary,
  type AuditProtocol,
  type TransactionStatusSummary,
  type ValidationHistoryItem,
} from '@/api/vatValidation'
import VatProtocolModal from '@/components/VatProtocolModal.vue'
import {
  getMatchMeaning,
  getBzstStatusDescription,
  formatFormalStatusCode,
  formatTrigger,
  isTransactionCheck,
} from '@/utils/vatCodes'
import { PERMISSIONS, hasAnyPermission } from '@/utils/roles'

const props = defineProps({
  mode: {
    type: String as () => 'address' | 'transaction' | 'auto',
    default: 'auto',
  },
  addressNr: { type: String, default: '' },
  transactionNr: { type: String, default: '' },
  identifier: { type: String, default: '' },
  embedded: { type: Boolean, default: false },
  customer: { type: String, default: '' },
  tenant: { type: String, default: '' },
})

const route = useRoute()
const authStore = getActivePinia() ? useAuthStore() : null
const toast = useToast()

// Mirrors the backend: triggering a single validation needs the VAT validation run
// permission, microtech data access or settings write access.
const canValidate = computed(() =>
  hasAnyPermission(authStore?.userInfo?.user?.permissions, [
    PERMISSIONS.VAT_VALIDATION_RUN,
    PERMISSIONS.MICROTECH_DATA_READ,
    PERMISSIONS.SETTINGS_WRITE,
  ]),
)

// Mode resolution: evaluate whether this view operates for an address or transaction
const isTransactionMode = computed(() => {
  if (props.mode === 'transaction') return true
  if (props.mode === 'address') return false
  if (props.transactionNr) return true
  if (props.addressNr) return false
  if (route.params.transactionNr) return true
  if (route.params.addressNr) return false

  // If using generic identifier (:id parameter in /embedded/vat-validation/:id)
  const id = props.identifier || (route.params.id as string) || ''
  const queryType = (route.query.type as string || '').toLowerCase()
  if (queryType === 'transaction' || queryType === 'vorgang') return true
  if (queryType === 'address' || queryType === 'adresse') return false

  // Auto-detect heuristic: If contains letters or dashes -> transaction; if pure numeric -> address
  if (id && /[A-Za-z-]/.test(id)) {
    return true
  }
  return false
})

const resolvedTransactionNr = computed(() => {
  if (!isTransactionMode.value) return ''
  return (
    props.transactionNr ||
    (route.params.transactionNr as string) ||
    props.identifier ||
    (route.params.id as string) ||
    ''
  )
})

const resolvedAddressNr = computed(() => {
  if (txSummary.value?.addressNr) {
    return txSummary.value.addressNr
  }
  return (
    props.addressNr ||
    (route.params.addressNr as string) ||
    (!isTransactionMode.value ? props.identifier || (route.params.id as string) : '') ||
    ''
  )
})

const isEmbedded = computed(() => {
  return props.embedded || route.query.embedded === 'true' || Boolean(route.meta.embedded)
})

const tenant = computed(() => {
  return (
    props.tenant ||
    (route.query.tenant as string) ||
    authStore?.userInfo?.activeTenant?.tenantName ||
    'default'
  )
})

const customer = computed(() => {
  return (
    props.customer ||
    (route.query.customer as string) ||
    authStore?.userInfo?.activeTenant?.customerNo ||
    'default'
  )
})

// State
const loading = ref(false)
const validating = ref(false)
const fetchError = ref<string | null>(null)
const statusSummary = ref<AddressStatusSummary | null>(null)
const txSummary = ref<TransactionStatusSummary | null>(null)
const latestProtocol = ref<AuditProtocol | null>(null)
const selectedProtocol = ref<AuditProtocol | null>(null)
const isProtocolModalOpen = ref(false)

const isUnvalidatedTransaction = computed(() => {
  if (!isTransactionMode.value) return false
  if (!txSummary.value) return true
  return txSummary.value.status === 'NOT_CHECKED' || !txSummary.value.validationId
})

const activeVatId = computed(() => {
  if (isTransactionMode.value && txSummary.value?.vatId) {
    return txSummary.value.vatId
  }
  return statusSummary.value?.testedVatId || ''
})

const activeValidationId = computed(() => {
  if (isTransactionMode.value && txSummary.value?.validationId) {
    return txSummary.value.validationId
  }
  return statusSummary.value?.lastValidationId || ''
})

const activeCheckTimestamp = computed(() => {
  if (isTransactionMode.value && !isUnvalidatedTransaction.value && txSummary.value?.timestamp) {
    return txSummary.value.timestamp
  }
  return statusSummary.value?.lastCheckTimestamp || ''
})

const effectiveStatus = computed(() => {
  if (isTransactionMode.value) {
    if (isUnvalidatedTransaction.value) return 'NOT_CHECKED'
    return txSummary.value?.status || 'NOT_CHECKED'
  }
  return statusSummary.value?.status || 'NOT_CHECKED'
})

const effectiveStatusDisplay = computed(() => {
  if (isTransactionMode.value) {
    if (isUnvalidatedTransaction.value) return 'Noch nicht geprüft'
    return txSummary.value?.statusDisplay || 'Noch nicht geprüft'
  }
  return statusSummary.value?.statusDisplay || 'Noch nicht geprüft'
})

const statusBadgeColor = computed(() => {
  const s = effectiveStatus.value
  switch (s) {
    case 'OFFICIALLY_CONFIRMED':
      return 'success'
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
      return 'error'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'NOT_CHECKED':
      return 'neutral'
    default:
      return 'neutral'
  }
})

const officialStatusCode = computed(() => {
  return latestProtocol.value?.result?.officialStatusCode || ''
})

const officialStatusMessage = computed(() => {
  if (latestProtocol.value?.result?.officialMessage) {
    return latestProtocol.value.result.officialMessage
  }
  if (officialStatusCode.value) {
    return getBzstStatusDescription(officialStatusCode.value)
  }
  return ''
})

const effectiveComparisonData = computed(() => {
  if (latestProtocol.value?.result) {
    return latestProtocol.value.result
  }
  return null
})

const isGermanFormal = computed(() => {
  if (
    latestProtocol.value?.validationType === 'FORMAL' ||
    latestProtocol.value?.interface === 'REGEX_DE_PATTERN' ||
    statusSummary.value?.validationType === 'FORMAL' ||
    statusSummary.value?.interface === 'REGEX_DE_PATTERN' ||
    txSummary.value?.validationType === 'FORMAL' ||
    txSummary.value?.interface === 'REGEX_DE_PATTERN'
  ) {
    return true
  }
  const s = effectiveStatus.value
  if (s === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || s === 'FORMALLY_INVALID') {
    return true
  }
  const vat = activeVatId.value
  if (vat && vat.toUpperCase().startsWith('DE')) {
    return true
  }
  const country = effectiveSubmittedData.value?.country
  if (
    country &&
    ['DE', 'DEU', 'DEUTSCHLAND', 'GERMANY'].includes(country.toUpperCase())
  ) {
    return true
  }
  return false
})

const isValidFormalVat = computed(() => {
  return /^DE\d{9}$/.test(activeVatId.value || '')
})

const hasAnyMatchData = computed(() => {
  const d = effectiveComparisonData.value
  if (!d) return false
  return Boolean(d.companyNameMatch || d.streetMatch || d.zipMatch || d.cityMatch)
})

function isItemGermanFormal(item: ValidationHistoryItem): boolean {
  if (item.validationType === 'FORMAL' || item.interface === 'REGEX_DE_PATTERN') {
    return true
  }
  if (item.status === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || item.status === 'FORMALLY_INVALID') {
    return true
  }
  if (
    item.officialStatusCode === 'FORMALLY_VALID' ||
    item.officialStatusCode === 'FORMAT_ERROR' ||
    item.officialStatusCode === 'MISSING_VAT_ID'
  ) {
    return true
  }
  return false
}

const effectiveSubmittedData = computed(() => {
  if (latestProtocol.value?.submittedData) {
    return latestProtocol.value.submittedData
  }
  if (statusSummary.value?.submittedData) {
    return statusSummary.value.submittedData
  }
  return null
})

const isCountryDomestic = computed(() => {
  const c = effectiveSubmittedData.value?.country
  if (!c) return true
  return ['DE', 'DEU', 'DEUTSCHLAND', 'GERMANY'].includes(c.toUpperCase().trim())
})

const historyItems = computed<ValidationHistoryItem[]>(() => {
  if (isTransactionMode.value && txSummary.value?.history && txSummary.value.history.length > 0) {
    return txSummary.value.history
  }
  return statusSummary.value?.history || []
})

type HistoryFilter = 'all' | 'address' | 'transaction'
const HISTORY_FILTER_STORAGE_KEY = 'vat-validation:history-filter'

function loadHistoryFilter(): HistoryFilter {
  try {
    const stored = localStorage.getItem(HISTORY_FILTER_STORAGE_KEY)
    if (stored === 'all' || stored === 'address' || stored === 'transaction') return stored
  } catch {
    // storage unavailable (private mode, embedded iframe) – fall back to default
  }
  return 'all'
}

const historyFilter = ref<HistoryFilter>(loadHistoryFilter())
watch(historyFilter, (value) => {
  try {
    localStorage.setItem(HISTORY_FILTER_STORAGE_KEY, value)
  } catch {
    // ignore – the filter is a per-viewer convenience only
  }
})

const transactionCheckCount = computed(() => historyItems.value.filter(isTransactionCheck).length)
const addressCheckCount = computed(() => historyItems.value.length - transactionCheckCount.value)
const hasBothCheckTypes = computed(() => addressCheckCount.value > 0 && transactionCheckCount.value > 0)

const historyFilterItems = computed(() => [
  { label: `Alle (${historyItems.value.length})`, value: 'all' },
  { label: `Adressprüfungen (${addressCheckCount.value})`, value: 'address', icon: 'i-lucide-map-pin' },
  { label: `Vorgangsprüfungen (${transactionCheckCount.value})`, value: 'transaction', icon: 'i-lucide-file-text' },
])

// Without both types the tabs are hidden, so a stored filter must not hide entries.
const filteredHistoryItems = computed<ValidationHistoryItem[]>(() => {
  if (!hasBothCheckTypes.value || historyFilter.value === 'all') return historyItems.value
  const wantTransaction = historyFilter.value === 'transaction'
  return historyItems.value.filter((item) => isTransactionCheck(item) === wantTransaction)
})

function getHistoryRowClass(item: ValidationHistoryItem): string {
  const focus = 'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary '
  if (isMatchingCurrentTransaction(item)) {
    return focus + 'bg-primary-50/70 hover:bg-primary-100/70 dark:bg-primary-950/40 dark:hover:bg-primary-900/40 border-l-4 border-l-primary'
  }
  if (isTransactionCheck(item)) {
    return focus + 'bg-secondary/5 hover:bg-secondary/10 dark:bg-secondary/10 dark:hover:bg-secondary/15 border-l-4 border-l-secondary'
  }
  return focus + 'hover:bg-gray-50/50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent'
}

function formatTransactionRef(item: ValidationHistoryItem): string {
  return [item.transactionType, item.transactionNo].filter(Boolean).join(' ')
}

function getReusedFromTitle(item: ValidationHistoryItem): string {
  const source = historyItems.value.find((h) => h.validationId === item.reusedFromValidationId)
  return source
    ? `Wiederverwendet aus Prüfung vom ${formatTimestamp(source.timestamp)}`
    : `Wiederverwendet aus Prüfung ${item.reusedFromValidationId}`
}

function isMatchingCurrentTransaction(item: ValidationHistoryItem): boolean {
  if (!isTransactionMode.value || !txSummary.value) return false
  if (item.validationId && txSummary.value.validationId && item.validationId === txSummary.value.validationId) {
    return true
  }
  if (item.validationId && txSummary.value.reusedFromValidationId && item.validationId === txSummary.value.reusedFromValidationId) {
    return true
  }
  return false
}

// Data Fetching
async function fetchData() {
  loading.value = true
  fetchError.value = null

  try {
    if (isTransactionMode.value) {
      if (!resolvedTransactionNr.value) {
        fetchError.value = 'Keine Vorgangsnummer angegeben.'
        loading.value = false
        return
      }

      const res = await vatValidationApi.getTransactionStatus(
        resolvedTransactionNr.value,
        tenant.value,
        customer.value,
      )
      txSummary.value = res.data
      if (res.data.latestAddressValidation) {
        statusSummary.value = res.data.latestAddressValidation
      }

      await loadLatestProtocol()
    } else {
      if (!resolvedAddressNr.value) {
        fetchError.value = 'Keine Adressnummer angegeben.'
        loading.value = false
        return
      }

      const res = await vatValidationApi.getAddressStatus(
        resolvedAddressNr.value,
        tenant.value,
        customer.value,
      )
      statusSummary.value = res.data
      await loadLatestProtocol()
    }
  } catch (err: any) {
    if (err?.response?.status === 404) {
      statusSummary.value = null
      txSummary.value = null
      latestProtocol.value = null
    } else {
      fetchError.value = err?.response?.data?.details || err?.message || 'Unbekannter Fehler aufgetreten.'
    }
  } finally {
    loading.value = false
  }
}

async function loadLatestProtocol() {
  const valId = activeValidationId.value
  const addr = resolvedAddressNr.value

  if (!valId || !addr) {
    latestProtocol.value = null
    return
  }

  try {
    const res = await vatValidationApi.getAuditProtocol(
      addr,
      valId,
      tenant.value,
      customer.value,
    )
    latestProtocol.value = res.data
  } catch (err) {
    console.warn('Failed to fetch latest audit protocol for qualified comparison:', err)
  }
}

async function triggerValidation() {
  validating.value = true
  try {
    if (isTransactionMode.value) {
      const txNr = resolvedTransactionNr.value
      if (!txNr) return

      const res = await vatValidationApi.validateTransaction(
        txNr,
        {
          addressNr: resolvedAddressNr.value,
          transactionType: txSummary.value?.transactionType,
          vatId: activeVatId.value,
        },
        tenant.value,
        customer.value,
      )
      txSummary.value = res.data
      if (res.data.latestAddressValidation) {
        statusSummary.value = res.data.latestAddressValidation
      }

      toast.add({
        title: 'Vorgangsprüfung erfolgreich',
        description: `Prüfergebnis für ${txNr}: ${res.data.statusDisplay}`,
        color: res.data.valid ? 'success' : 'warning',
      })
    } else {
      const addrNr = resolvedAddressNr.value
      if (!addrNr) return

      const res = await vatValidationApi.validateAddress(
        addrNr,
        undefined,
        tenant.value,
        customer.value,
      )

      toast.add({
        title: 'Prüfung erfolgreich abgeschlossen',
        description: `Ergebnis für Adresse ${addrNr}: ${res.data.statusDisplay}`,
        color: res.data.result?.valid ? 'success' : 'warning',
      })
    }

    await fetchData()
  } catch (err: any) {
    toast.add({
      title: 'Prüfung fehlgeschlagen',
      description: err?.response?.data?.details || err?.message || 'Fehler bei der Validierung.',
      color: 'error',
    })
  } finally {
    validating.value = false
  }
}

async function viewProtocol(validationId?: string) {
  if (!validationId) return

  const addr = resolvedAddressNr.value
  if (!addr) {
    toast.add({
      title: 'Fehler',
      description: 'Keine Adressnummer zur Validierung gefunden.',
      color: 'error',
    })
    return
  }

  try {
    const res = await vatValidationApi.getAuditProtocol(
      addr,
      validationId,
      tenant.value,
      customer.value,
    )
    selectedProtocol.value = res.data
    isProtocolModalOpen.value = true
  } catch (err: any) {
    toast.add({
      title: 'Protokoll konnte nicht geladen werden',
      description: err?.message || 'Fehler beim Laden des Prüfprotokolls.',
      color: 'error',
    })
  }
}

// Helpers
function formatTimestamp(ts?: string): string {
  if (!ts) return '-'
  try {
    const d = new Date(ts)
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ts
  }
}

function getMatchIcon(val?: string): string {
  switch (val) {
    case 'A':
      return 'i-lucide-check-circle-2'
    case 'B':
      return 'i-lucide-x-circle'
    case 'C':
    case 'D':
      return 'i-lucide-alert-circle'
    default:
      return 'i-lucide-minus-circle'
  }
}

function getMatchColor(val?: string): string {
  switch (val) {
    case 'A':
      return 'text-emerald-600 dark:text-emerald-400'
    case 'B':
      return 'text-red-500 dark:text-red-400'
    case 'C':
    case 'D':
      return 'text-amber-500 dark:text-amber-400'
    default:
      return 'text-gray-400'
  }
}

function getMatchLabel(val?: string): string {
  return getMatchMeaning(val)
}

function getHistoryItemColor(status: string, item?: ValidationHistoryItem): any {
  if (item && hasMatchResults(item) && !item.allMatchesCodeA && status === 'OFFICIALLY_CONFIRMED') {
    return 'warning'
  }
  switch (status) {
    case 'OFFICIALLY_CONFIRMED':
      return 'success'
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
      return 'error'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'NOT_CHECKED':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function getHistoryItemDisplay(item: ValidationHistoryItem): string {
  if (hasMatchResults(item) && !item.allMatchesCodeA && item.status === 'OFFICIALLY_CONFIRMED') {
    return 'Ausland: Eingeschränkt gültig'
  }
  return item.statusDisplay || item.status
}

function hasMatchResults(item: ValidationHistoryItem): boolean {
  return Boolean(
    item.companyNameMatch || item.streetMatch || item.zipMatch || item.cityMatch,
  )
}

function getAMatchPercentage(item: ValidationHistoryItem): number {
  if (item.allMatchesCodeA === true) {
    return 100
  }
  const fields = [item.companyNameMatch, item.streetMatch, item.zipMatch, item.cityMatch]
  const countA = fields.filter((m) => m === 'A').length
  return Math.round((countA / 4) * 100)
}

function getAMatchPercentageColor(item: ValidationHistoryItem): string {
  const pct = getAMatchPercentage(item)
  if (pct === 100) {
    return 'text-green-600 dark:text-green-400 font-bold'
  }
  if (pct > 0) {
    return 'text-amber-600 dark:text-amber-400 font-semibold'
  }
  return 'text-red-600 dark:text-red-400 font-semibold'
}

function getAMatchPercentageTitle(item: ValidationHistoryItem): string {
  const pct = getAMatchPercentage(item)
  const parts: string[] = []
  if (item.companyNameMatch) parts.push(`Firmenname: ${item.companyNameMatch}`)
  if (item.streetMatch) parts.push(`Straße: ${item.streetMatch}`)
  if (item.zipMatch) parts.push(`PLZ: ${item.zipMatch}`)
  if (item.cityMatch) parts.push(`Ort: ${item.cityMatch}`)

  if (parts.length > 0) {
    return `A-Status: ${pct}% (${parts.join(', ')})`
  }
  return `A-Status: ${pct}%`
}

onMounted(() => {
  fetchData()
})

watch(
  [
    () => props.addressNr,
    () => props.transactionNr,
    () => props.identifier,
    () => route.params.addressNr,
    () => route.params.transactionNr,
    () => route.params.id,
    () => route.query.tenant,
    () => route.query.customer,
  ],
  () => {
    fetchData()
  },
)
</script>
